export type TLocalized = { uz: string; en: string; ru: string };

export type TMapUrls = {
  yandex?: string;
  google?: string;
  twoGis?: string;
};

export type TBranch = {
  slug: string;
  /** Display name in each locale */
  name: TLocalized;
  /** City this branch belongs to */
  city: TLocalized;
  /** Street address */
  address: TLocalized;
  /** Nearest landmark (e.g. metro station, mall) */
  landmark?: TLocalized;
  /** Geo coordinates */
  geo: { lat: number; lng: number };
  /** External map service URLs */
  mapUrls?: TMapUrls;
  /** Branch-specific phone (optional). Defaults to main number when absent. */
  phone?: string;
  /** Cover file name inside /branches/covers (defaults to `${slug}.webp`) */
  cover?: string;
};

export const MAIN_PHONE = "+998787777707";

/**
 * Inter Nation English School — 16 branches
 * (11 in Tashkent, 2 in Samarkand, Andijan, Fergana, Namangan).
 *
 * Online program is included in the 16 but has no physical location, so it is
 * not listed here. Every user-facing branch count must come from
 * BRANCH_COUNT / BRANCH_CITIES below (and the BRANCH_COUNT* locale keys).
 */
export const BRANCHES: TBranch[] = [
  {
    slug: "sergeli",
    name: { uz: "Sergeli", en: "Sergeli", ru: "Сергели" },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Yangi Sergeli köçasi, 21B",
      en: "21B Yangi Sergeli St.",
      ru: "Ул. Янги Сергели, 21Б",
    },
    landmark: {
      uz: 'Sergeli tumani jinoiy işlar sudi qarşisida, "Credit Asia Technics" yonida',
      en: 'Opposite Sergeli District Criminal Court, near "Credit Asia Technics"',
      ru: 'Напротив Уголовного суда Сергелийского района, рядом с "Credit Asia Technics"',
    },
    geo: { lat: 41.22171, lng: 69.222335 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLvw5IM1",
      google: "https://maps.app.goo.gl/CsvTB6zhCrStyGmw9",
      twoGis: "https://go.2gis.com/7TWkG",
    },
  },
  {
    slug: "yunusobod",
    name: { uz: "Yunusobod", en: "Yunusobod", ru: "Юнусабад" },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Ahmad Doniş köçasi, 1A",
      en: "1A Akhmad Donish St.",
      ru: "Ул. Ахмада Дониша, 1А",
    },
    landmark: {
      uz: "Megaplanet, Yunusobod 13",
      en: "Megaplanet, Yunusobod 13",
      ru: "Мегапланет, Юнусабад 13",
    },
    geo: { lat: 41.367351, lng: 69.293553 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CPebjYLX",
      google: "https://maps.app.goo.gl/VmM7b8TeUn6rDN7v6",
      twoGis: "https://go.2gis.com/5dhiR",
    },
  },
  {
    slug: "chilonzor",
    name: { uz: "Çilonzor", en: "Chilonzor", ru: "Чиланзар" },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Bunyodkor köçasi, 52/2",
      en: "52/2 Bunyodkor St.",
      ru: "Ул. Бунёдкор, 52/2",
    },
    landmark: {
      uz: "KFC (Çilonzor metro bekati)",
      en: "KFC (Chilanzar metro station)",
      ru: "KFC (метро Чиланзар)",
    },
    geo: { lat: 41.275371, lng: 69.204323 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLvwM6k2",
      google: "https://maps.app.goo.gl/BczfhMswibSMXSWw9",
      twoGis: "https://go.2gis.com/k343N",
    },
  },
  {
    slug: "beruniy",
    name: { uz: "Beruniy", en: "Beruniy", ru: "Беруни" },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Beruniy köçasi, 5",
      en: "5 Beruniy St.",
      ru: "Ташкент, ул. Беруни, 5",
    },
    landmark: {
      uz: "Beruniy metro bekati",
      en: "Beruniy metro station",
      ru: "Станция метро Беруни",
    },
    geo: { lat: 41.344578, lng: 69.207337 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLvwjHpl",
      google: "https://maps.app.goo.gl/iZg4bgSX1hb1Tccj8",
      twoGis: "https://go.2gis.com/9JKiX",
    },
  },
  {
    slug: "maksim",
    name: { uz: "Maksim Gorkiy", en: "Maksim Gorky", ru: "Максим Горький" },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Buyuk Ipak Yöli köçasi, 105B",
      en: "105B Buyuk Ipak Yuli St.",
      ru: "Ул. Буюк ипак йули, 105Б",
    },
    landmark: {
      uz: "KFC Fayz, 2-qavat",
      en: "KFC Fayz, 2nd floor",
      ru: "KFC Файз, 2-й этаж",
    },
    geo: { lat: 41.326731, lng: 69.33071 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLvwVKLB",
      google: "https://maps.app.goo.gl/7soYS5JazvRzcpby9",
      twoGis: "https://go.2gis.com/mqRk6",
    },
  },
  {
    slug: "drujba",
    name: {
      uz: "Xalqlar döstligi",
      en: "Khalqlar Do'stligi (Drujba)",
      ru: "Дружба Народов",
    },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Islom Karimov köçasi, 8/1",
      en: "8/1 Islam Karimov St.",
      ru: "Ул. Ислама Каримова, 8/1",
    },
    landmark: {
      uz: 'Xalqlar döstligi metro bekati, sobiq "UzBowling"',
      en: 'Khalqlar Do\'stligi metro station, former "UzBowling"',
      ru: 'Метро Дружба Народов, бывший "UzBowling"',
    },
    geo: { lat: 41.3112977, lng: 69.245213 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLvsA2n6",
      google: "https://maps.app.goo.gl/oJwFF5B2S6hampQg7",
      twoGis: "https://go.2gis.com/vfPmS",
    },
  },
  {
    slug: "ganga",
    name: { uz: "Ganga", en: "Ganga", ru: "Ганга" },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Furqat köçasi, 1",
      en: "1 Furkat St.",
      ru: "Ул. Фуркат, 1",
    },
    landmark: {
      uz: "KFC Rossini, 2-qavat",
      en: "KFC Rossini, 2nd floor",
      ru: "KFC Россини, 2-й этаж",
    },
    geo: { lat: 41.327409, lng: 69.247543 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLvwzBYs",
      google: "https://maps.app.goo.gl/N6oJYyopFL7cNtC7A",
      twoGis: "https://go.2gis.com/jQoek",
    },
  },
  {
    slug: "minor",
    name: { uz: "Minor", en: "Minor", ru: "Минор" },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Amir Temur köçasi, 86",
      en: "86 Amir Temur St.",
      ru: "Ул. Амир Темур, 86",
    },
    landmark: {
      uz: 'Sobiq "Qozoğiston" kinoteatri yoki Minor metro bekati',
      en: 'Former "Kazakhstan" cinema or Minor metro station',
      ru: 'Бывший кинотеатр "Казахстан" или станция метро Минор',
    },
    geo: { lat: 41.329179, lng: 69.283081 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLvsIVpD",
      google: "https://maps.app.goo.gl/1yyhivz3UWF9PqKj9",
      twoGis: "https://go.2gis.com/8FnDv",
    },
  },
  {
    slug: "oybek",
    name: { uz: "Oybek", en: "Oybek", ru: "Ойбек" },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Afrosiyob köçasi, 16",
      en: "16 Afrosiyob St.",
      ru: "Ул. Афросиаб, 16",
    },
    landmark: {
      uz: 'Sobiq "Angel\'s Food" (Oybek metro bekati)',
      en: 'Former "Angel\'s Food" (Oybek metro station)',
      ru: 'Бывший "Angel\'s Food" (метро Ойбек)',
    },
    geo: { lat: 41.299104, lng: 69.272534 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLvsbJ57",
      google: "https://maps.app.goo.gl/vtVCK7nQMFYmu7P88",
      twoGis: "https://go.2gis.com/lB2FX",
    },
  },
  {
    slug: "west",
    name: { uz: "Westminster", en: "Westminster", ru: "Вестминстер" },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Istiqlol köçasi, 14",
      en: "14 Istiklol St.",
      ru: "Ул. Истиклол, 14",
    },
    landmark: {
      uz: "Westminster universiteti, Özbekneftegaz",
      en: "Westminster University, Uzbekneftegaz",
      ru: "Вестминстерский университет, Узбекнефтегаз",
    },
    geo: { lat: 41.3062919, lng: 69.284173 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLvsi6Ir",
      google: "https://maps.app.goo.gl/7qUM2mLhzrerfw7k8",
      twoGis: "https://go.2gis.com/3C4JV",
    },
  },
  {
    slug: "c1",
    name: { uz: "C1", en: "C1", ru: "C1" },
    city: { uz: "Toşkent", en: "Tashkent", ru: "Ташкент" },
    address: {
      uz: "Şahrisabz köçasi, 1, C1",
      en: "1 Shakhrisabz St., C1",
      ru: "Ц1, ул. 1 Шахрисабз",
    },
    landmark: {
      uz: 'Sobiq "Detsky Mir"',
      en: 'Former "Detsky Mir"',
      ru: 'Бывший "Детский мир"',
    },
    geo: { lat: 41.313824, lng: 69.286838 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLvwAF9-",
      google: "https://maps.app.goo.gl/E4obtq1QkGQu7nqM6",
      twoGis: "https://go.2gis.com/8vAad",
    },
  },
  {
    slug: "samarqand-gagarin",
    cover: "samarqand_gagarin.webp",
    name: { uz: "Samarqand", en: "Samarkand", ru: "Самарканд" },
    city: { uz: "Samarqand", en: "Samarkand", ru: "Самарканд" },
    address: {
      uz: "Samarqand, Gagarin köçasi, 128",
      en: "Samarkand, 128 Gagarin St.",
      ru: "Самарканд, ул. Гагарина, 128",
    },
    landmark: {
      uz: '"JAHON PALACE" mehmonxonasi röparasida',
      en: 'Opposite "JAHON PALACE" hotel',
      ru: 'Напротив отеля "JAHON PALACE"',
    },
    geo: { lat: 39.657384, lng: 66.93573 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CLv1E8Z2",
      google: "https://maps.app.goo.gl/ziSc1qRhzcy7gQEZ8",
      twoGis: "https://go.2gis.com/OIT6Q",
    },
  },
  {
    slug: "samarqand-dagbitskiy",
    cover: "samarqand_dahbed.webp",
    name: {
      uz: "Samarqand Dagbitskiy",
      en: "Samarkand Dagbitskiy",
      ru: "Самарканд Дагбитский",
    },
    city: { uz: "Samarqand", en: "Samarkand", ru: "Самарканд" },
    address: {
      uz: "Samarqand, Rudakiy köçasi, 234",
      en: "Samarkand, Rudakiy street, 234",
      ru: "Самарканд, улица Рудакий, 234",
    },
    landmark: {
      uz: '"Hotel Bek Samarkand" yonida',
      en: "Near Hotel Bek Samarkand",
      ru: "Рядом с гостиницей «Hotel Bek Samarkand»",
    },
    geo: { lat: 39.66902, lng: 66.968889 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CXAAMUjI",
      google: "https://maps.app.goo.gl/tf7Qpm9nHaky2oHRA",
      twoGis: "https://2gis.ru/samarkand/geo/70030077140349150",
    },
  },
  {
    slug: "andijan",
    cover: "andijon.webp",
    name: { uz: "Andijon", en: "Andijan", ru: "Андижан" },
    city: { uz: "Andijon", en: "Andijan", ru: "Андижан" },
    address: {
      uz: "Andijon, Bobur şoh köçasi, 40",
      en: "Andijan, 40 Bobur Street",
      ru: "Андижан, ул. Бобура, 40",
    },
    landmark: {
      uz: 'Yangi bozor hududidagi "Mumtoz" binosi',
      en: '"Mumtoz" Building near the New Bazaar',
      ru: 'Здание "Мумтоз" в районе Нового базара',
    },
    geo: { lat: 40.75828, lng: 72.353183 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CPSrePls",
      google: "https://maps.app.goo.gl/RrV4C6sHREDAJKKc7",
      twoGis: "https://go.2gis.com/e6t2g",
    },
  },
  {
    slug: "fargona",
    name: { uz: "Farğona", en: "Fergana", ru: "Фергана" },
    city: { uz: "Farğona", en: "Fergana", ru: "Фергана" },
    address: {
      uz: "Farğona, Sayilgoh köçasi, 47A",
      en: "Fergana, Sayilgoh Street, 47A",
      ru: "Фергана, улица Сайилгох, 47A",
    },
    landmark: {
      uz: "Ma’rifat mahalla fuqarolar yiğini, Sayilgoh köçasi",
      en: "Ma’rifat Mahalla Citizens’ Assembly, Sayilgoh Street",
      ru: "Сход граждан махалли «Ма’рифат», улица Сайилгох",
    },
    geo: { lat: 40.387691, lng: 71.789037 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CTXMvO9Z",
      google: "https://maps.app.goo.gl/Grb7y1PgsWZXrnJGA",
      twoGis: "https://go.2gis.com/21P6G",
    },
  },
  {
    slug: "namangan",
    name: { uz: "Namangan", en: "Namangan", ru: "Наманган" },
    city: { uz: "Namangan", en: "Namangan", ru: "Наманган" },
    address: {
      uz: "Namangan, Alişer Navoiy köçasi, 31A",
      en: "Namangan, Alisher Navoi Street, 31A",
      ru: "Наманган, улица Алишера Навои, 31A",
    },
    landmark: {
      uz: "Marğilon mahalla fuqarolar yiğini",
      en: "Margilan Mahalla Citizens’ Assembly",
      ru: "Сход граждан махалли «Маргилан»",
    },
    geo: { lat: 40.992832, lng: 71.670115 },
    mapUrls: {
      yandex: "https://yandex.uz/maps/-/CTdkFQJz",
      google: "https://maps.app.goo.gl/uRfKaVqcxataGNfK6",
      twoGis: "https://2gis.ru/geo/71.670088,40.992824",
    },
  },
];

export const getBranchBySlug = (slug: string): TBranch | undefined =>
  BRANCHES.find((b) => b.slug === slug);

export const getBranchCoverUrl = (b: TBranch) =>
  `/branches/covers/${b.cover ?? `${b.slug}.webp`}`;

export const getBranchByCoverUrl = (url?: string): TBranch | undefined =>
  url ? BRANCHES.find((b) => getBranchCoverUrl(b) === url) : undefined;

/** Single source of truth for "how many branches" (Online included). */
export const BRANCH_COUNT = BRANCHES.length;
